import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { useCart } from "@/contexts/CartContext";

/**
 * Landing page after Stripe Checkout redirect.
 * Polls /api/payments/stripe/status/{session_id} every ~2s until paid or timeout.
 *
 * iter138.1 — reads cart_tier off the order to match Cart + Checkout dark theme.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [order, setOrder] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const maxAttempts = 30;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Cart is now cleared ONLY on confirmed payment success — real
          // customer bailed mid-payment previously and lost their cart. Only
          // clears here for Stripe; Manual clears in ManualPayment "I've sent"
          try { clear(); } catch { /* best-effort */ }
          setState("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("error"); return; }
      } catch {/* transient — try again */}
      if (attempts >= maxAttempts) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Fetch order (best-effort) to know if it's a Select-tier cart so we can theme.
  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
      } catch {/* ignore — falls back to BBK palette */}
    })();
  }, [orderId]);

  const isSelect = order?.cart_tier === "select";
  useSetSelectTheme(isSelect);

  const shell = isSelect ? "bg-[#F1E5C7] text-[#1B1614] min-h-screen" : "";
  const muted = isSelect ? "text-[#1B1614]/75" : "text-stone";
  const strong = isSelect ? "text-[#1B1614]" : "text-charcoal";
  const primaryBtn = isSelect
    ? "bg-[#DDA94E] text-[#1B1614] hover:bg-[#F1E5C7]"
    : "bg-charcoal text-bone hover:bg-oxblood";
  const secondaryBtn = isSelect
    ? "border border-[#DDA94E]/50 text-[#1B1614] hover:border-[#DDA94E] hover:text-[#6E521A]"
    : "border border-borderSubtle text-charcoal hover:border-oxblood hover:text-oxblood";
  const accent = isSelect ? "text-[#6E521A]" : "text-oxblood";
  const spinnerColor = isSelect ? "text-[#6E521A]" : "text-oxblood";
  const paidBadge = isSelect
    ? "bg-[#DDA94E] text-[#1B1614]"
    : "bg-oxblood text-bone";

  return (
    <div className={shell}>
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 size={44} className={`mx-auto mb-6 animate-spin ${spinnerColor}`} strokeWidth={1.5}/>
            <h1 className={`font-serif text-4xl md:text-5xl mb-4 ${strong}`}>Confirming your payment…</h1>
            <p className={`leading-relaxed ${muted}`}>Stripe is sending us confirmation. This usually takes a couple seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className={`mx-auto mb-6 w-16 h-16 rounded-full flex items-center justify-center ${paidBadge}`}>
              <Check size={30} strokeWidth={2.5}/>
            </div>
            <p className={`overline mb-3 ${accent}`}>Payment received</p>
            <h1 className={`font-serif text-4xl md:text-5xl mb-4 ${strong}`}>Thank you.</h1>
            <p className={`leading-relaxed mb-8 ${muted}`}>
              Your order is paid and moves into fulfilment. We'll email tracking the moment it ships.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              {orderId && (
                <Link to={`/orders/${orderId}`} className={`px-6 py-3 tracking-[0.22em] text-xs uppercase rounded-full transition-colors ${primaryBtn}`} data-testid="paid-order-link">
                  View order
                </Link>
              )}
              <Link to="/shop" className={`px-6 py-3 tracking-[0.22em] text-xs uppercase rounded-full transition-colors ${secondaryBtn}`}>
                Back to catalog
              </Link>
            </div>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertCircle size={44} className={`mx-auto mb-6 ${accent}`} strokeWidth={1.5}/>
            <h1 className={`font-serif text-4xl md:text-5xl mb-4 ${strong}`}>Still confirming…</h1>
            <p className={`leading-relaxed mb-8 ${muted}`}>
              Stripe hasn't confirmed your payment yet. Don't worry — nothing is lost.
              {orderId && <> Check your order page in a minute; if it hasn't updated, email <a href="mailto:support@bulkbuykits.com" className={`underline ${accent}`}>support@bulkbuykits.com</a>.</>}
            </p>
            {orderId && (
              <Link to={`/orders/${orderId}`} className={`px-6 py-3 tracking-[0.22em] text-xs uppercase rounded-full transition-colors ${primaryBtn}`}>
                Go to your order
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
